import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import style from './EW.module.css'
import { FieldConfig, WarningTypes } from './EWType'
import { CalenderIcon, DepartmentIcon, DocumentIcon, DownIcon, IdIcon, MobileIcon, PrimaryCourseIcon, UpIcon } from '../iconSet/IconSet'
import InputGroup from '../inputComp/InputGroup'
import { LightningRiskLevel, PrimaryCause } from './EWFuntion'
import { InputGroupPropsTypes } from '../inputComp/InputGroupType'
import AreaTable, { AreaTableHandle } from '../areaTable/AreaTable'
import { WarningType } from '../areaTable/AreaTableType'
import { BASE_URL, generateWarningKey, submitEarlyWarning, generateWarningMessage } from './EwSubmission'
import { SelectItemTypes } from '../../CommonTypes'
import { useUserData } from '../../context/UserDataContext'
import AlertDialog from '../popupComp/AlertDialog'
import LoadingScreen from '../loadingScreenComp/LoadingScreen'

type EWProps = {
    flowId: string
    flowName: string
    department: string
    hazardType: string
    warningName: string
    warningType: string
}

// same token your other pages use
const getAccessToken = (): string => localStorage.getItem('access_token') ?? ''

// shape returned by /identity_manager/api/v1/getalldepartments
interface Department {
    departmentid: string | number
    departmentname: string
}

// datetime-local inputs only show 'YYYY-MM-DDTHH:mm', so convert whatever the field gives us
const toDateTimeLocal = (value: string): string => {
    if (!value) return ''
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return value.slice(0, 16)
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(value)) return value.replace(' ', 'T').slice(0, 16)

    const d = new Date(value)
    if (isNaN(d.getTime())) return ''
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const EW = ({ flowId, flowName, hazardType, warningName, warningType }: EWProps) => {

    const { department, contact } = useUserData()

    const [alertDialogState, setAlertDialogState] = useState(false);
    const [alertTitle, setAlertTitle] = useState("");
    const [alertDescription, setAlertDescription] = useState("");

    const [isLoading, setIsLoading] = useState<boolean>(false)

    const [alertYesCallback, setAlertYesCallback] = useState<
        (() => void) | undefined
    >(undefined); // for Yes
    const [alertOkCallback, setAlertOkCallback] = useState<
        (() => void) | undefined
    >(undefined); // for OK

    const showAlertDialog = (
        title: string,
        description: string,
        yesCallback?: () => void,
        okCallback?: () => void
    ) => {
        setAlertTitle(title);
        setAlertDescription(description);
        setAlertYesCallback(() => yesCallback); // Will show Yes/Cancel if defined
        setAlertOkCallback(() => okCallback); // Will trigger only on OK
        setAlertDialogState(true);
    };

    const flowIdRef = useRef<HTMLInputElement | null>(null)
    const flowNameRef = useRef<HTMLInputElement | null>(null)
    const warningIdRef = useRef<HTMLInputElement | null>(null)
    const requestNameRef = useRef<HTMLInputElement | null>(null)
    const validityStartRef = useRef<HTMLInputElement | null>(null)
    const validityEndRef = useRef<HTMLInputElement | null>(null)
    const contactNumberRef = useRef<HTMLInputElement | null>(null)
    const preRequestDocumentRef = useRef<HTMLInputElement | null>(null)
    const primaryCauseRef = useRef<HTMLSelectElement | null>(null)
    const lightningRiskLevelRef = useRef<HTMLSelectElement | null>(null)
    const dateRef = useRef<HTMLInputElement | null>(null)
    const requestersDepartmentRef = useRef<HTMLSelectElement | null>(null)

    // two separate textareas, so two separate refs (they used to share one, so the second overwrote the first)
    const generalMessageRef = useRef<HTMLTextAreaElement | null>(null)
    const customMessageRef = useRef<HTMLTextAreaElement | null>(null)

    const reportLinkRef = useRef<HTMLInputElement | null>(null)

    const areaTableRef = useRef<AreaTableHandle | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [fileName, setFileName] = useState<string>('Upload')
    const [isMoreMassageOptionOpen, setIsMoreMessageOptionOpen] = useState<boolean>(false)

    const [validityStart, setValidityStart] = useState<string>('')
    const [validityEnd, setValidityEnd] = useState<string>('')

    const [departments, setDepartments] = useState<SelectItemTypes[]>([])

    const [warningId, setWarningId] = useState<string>('')
    const [warningIdError, setWarningIdError] = useState<string>('')
    const requestedKeyFor = useRef<string | null>(null)   // hazard type we already asked a key for

    const [submitting, setSubmitting] = useState<boolean>(false)
    const [submitStatus, setSubmitStatus] = useState<{ ok: boolean; text: string } | null>(null)

    const fieldsRef = useRef<HTMLDivElement | null>(null)

    // read the live value of a field: by ref first, then by its name inside the wrapper
    const readField = (
        ref: React.RefObject<HTMLInputElement | HTMLSelectElement | null>,
        name: string
    ): string => {
        const el = ref.current ?? fieldsRef.current?.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="${name}"]`)
        return el?.value ?? ''
    }

    const syncValidity = () => {
        setValidityStart(toDateTimeLocal(readField(validityStartRef, 'validityStartTime')))
        setValidityEnd(toDateTimeLocal(readField(validityEndRef, 'validityEndTime')))
    }

    // catches changes made in code (no events) as well as typing
    useEffect(() => {
        syncValidity()
        const timer = setInterval(syncValidity, 300)
        return () => clearInterval(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hazardType])

    // load the department list for the "Requester's Department" select
    useEffect(() => {
        let active = true

        fetch(BASE_URL + '/identity_manager/api/v1/getalldepartments', {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${getAccessToken()}`,
                'Content-Type': 'application/json',
            },
        })
            .then((res) => {
                if (!res.ok) throw new Error(`Failed to load departments (${res.status})`)
                return res.json()
            })
            .then((data: Department[]) => {
                if (!active) return
                setDepartments(data.map((item) => ({ value: item.departmentid, name: item.departmentname, isDisabled: false })) as SelectItemTypes[])
            })
            .catch((err) => console.error('Error fetching departments:', err))

        return () => { active = false }
    }, [])

    // get a new warning id when the page opens (and again if the hazard type changes).
    // The ref guard stops React StrictMode's double effect run from using up two numbers.
    const loadWarningKey = () => {
        if (!hazardType) return
        requestedKeyFor.current = hazardType
        setWarningId('')
        setWarningIdError('')

        generateWarningKey(flowId, getAccessToken())
            .then((key) => {
                if (requestedKeyFor.current === hazardType) setWarningId(key)   // ignore stale answers
            })
            .catch((err) => {
                if (requestedKeyFor.current !== hazardType) return
                requestedKeyFor.current = null    // allow a retry
                setWarningIdError(err instanceof Error ? err.message : 'Could not generate warning id.')
            })
    }

    useEffect(() => {
        if (requestedKeyFor.current === hazardType) return
        loadWarningKey()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hazardType])

    const nowLocal = (): string => {
        const d = new Date()
        const pad = (n: number) => String(n).padStart(2, '0')
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    }
    const [createdAt] = useState<string>(nowLocal)

    const allFields: FieldConfig[] = [
        {
            type: 'text',
            icon: <IdIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Getting Flow Id...',
            label: 'Flow Id',
            name: 'flow_id',
            required: true,
            componentInputElement: flowIdRef,
            disabled: true,
            showFor: ['HRW', 'LSW', 'HWN', 'FLW', 'LIG', 'MAW', 'TSW', 'CUS', 'GUS']
        },
        {
            type: 'text',
            icon: <IdIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Getting Flow Name...',
            label: 'Flow Id',
            name: 'flow_id',
            required: true,
            componentInputElement: flowNameRef,
            disabled: true,
            showFor: ['HRW', 'LSW', 'HWN', 'FLW', 'LIG', 'MAW', 'TSW', 'CUS', 'GUS']
        },
        {
            type: 'text',
            icon: <IdIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Generating warning id...',
            label: 'Warning Id',
            name: 'warningId',
            required: true,
            value: warningId,
            componentInputElement: warningIdRef,
            disabled: true,
            showFor: ['HRW', 'LSW', 'HWN', 'FLW', 'LIG', 'MAW', 'TSW', 'CUS', 'GUS']
        },
        {
            type: 'date-time',
            icon: <CalenderIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Enter start valid period',
            label: 'Validity Start Time',
            name: 'validityStartTime',
            required: true,
            componentInputElement: validityStartRef,
            showFor: ['HRW', 'LSW', 'HWN', 'FLW', 'LIG', 'MAW', 'TSW', 'CUS', 'GUS']
        },
        {
            type: 'date-time',
            icon: <CalenderIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Enter end valid period',
            label: 'Validity End Time',
            name: 'validityEndTime',
            required: true,
            componentInputElement: validityEndRef,
            showFor: ['HRW', 'LSW', 'HWN', 'FLW', 'LIG', 'MAW', 'TSW', 'CUS', 'GUS']
        },
        {
            type: 'text',
            icon: <MobileIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Enter Contact number',
            label: 'Contact Number',
            name: 'contactNumber',
            required: false,
            componentInputElement: contactNumberRef,
            showFor: ['HRW', 'LSW', 'HWN', 'FLW', 'LIG', 'MAW', 'TSW', 'CUS', 'GUS']
        },
        {
            type: 'text',
            icon: <DocumentIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Enter pre-request document',
            label: 'Pre-request Document',
            name: 'preRequestDocument',
            required: false,
            componentInputElement: preRequestDocumentRef,
            showFor: ['HRW', 'LSW', 'LIG', 'CUS', 'GUS', 'MAW']
        },
        {
            type: 'select',
            icon: <PrimaryCourseIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            label: 'Primary Cause',
            name: 'PrimaryCause',
            required: false,
            componentSelectElement: primaryCauseRef,
            placeholder: 'Not selected',
            optionList: PrimaryCause,
            showFor: ['HRW', 'LSW', 'HWN', 'CUS', 'GUS']
        },
        {
            type: 'select',
            icon: <IdIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            name: 'LightningRiskLevel',
            label: 'Lightning Risk Level',
            required: false,
            placeholder: "No Risk",
            componentSelectElement: lightningRiskLevelRef,
            optionList: LightningRiskLevel,
            showFor: ['HRW', 'LSW']
        },
        {
            type: 'date-time',
            icon: <CalenderIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Enter end valid period',
            label: 'Date',
            name: 'date',
            required: true,
            disabled: true,
            value: createdAt,
            componentInputElement: dateRef,
            showFor: ['HRW', 'LSW', 'HWN', 'FLW', 'LIG', 'MAW', 'TSW', 'CUS', 'GUS']
        },
        {
            type: 'select',
            icon: <DepartmentIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            label: 'Requester\'s Department',
            name: 'requestersDepartment',
            required: false,
            placeholder: 'Not selected',
            componentSelectElement: requestersDepartmentRef,
            optionList: departments,
            disabled: true,
            showFor: ['HRW', 'LSW', 'HWN', 'FLW', 'LIG', 'MAW', 'TSW', 'CUS', 'GUS']
        }
    ]

    const buildDataMap = (requestType: WarningTypes): InputGroupPropsTypes[] =>
        allFields
            .filter(f => !f.showFor || f.showFor.includes(requestType))
            .map(({ showFor, overrides, ...field }) => ({
                ...field,
                ...overrides?.[requestType],   // apply per-type changes if any
            }))

    const dataMap = useMemo(() => buildDataMap(hazardType as WarningTypes), [hazardType, warningId, departments]);

    // also write it straight into the input, in case InputGroup doesn't react to the value prop
    useEffect(() => {
        if (warningIdRef.current) warningIdRef.current.value = warningId
    }, [warningId, dataMap])

    // pre-select the user's department once the options exist
    useEffect(() => {
        if (requestersDepartmentRef.current) requestersDepartmentRef.current.value = department
        if (contactNumberRef.current) contactNumberRef.current.value = contact
        if (flowIdRef.current) flowIdRef.current.value = flowId
        if (flowNameRef.current) flowNameRef.current.value = flowName
    }, [department, contact, dataMap])

    const [generating, setGenerating] = useState<boolean>(false)

    const handleGenerateEWMessageData = async () => {
        setSubmitStatus(null)
    
        const areas = areaTableRef.current?.getSelectedRows() ?? []
    
        if (!warningId) {
            setSubmitStatus({ ok: false, text: 'Warning id has not been generated yet.' })
            return
        }
        if (!validityStart || !validityEnd) {
            setSubmitStatus({ ok: false, text: 'Validity start and end time are required.' })
            return
        }
        if (areas.length === 0) {
            setSubmitStatus({ ok: false, text: 'Select at least one area.' })
            return
        }
    
        setGenerating(true)
        try {
            const message = await generateWarningMessage({
                triggerType: 'MANUAL',
                type: hazardType,
                warningNumber: warningId,
                warningId: flowId,
                requestName: readField(requestNameRef, 'FlowName') || warningName,
                contactNumber: readField(contactNumberRef, 'contactNumber'),
                preRequestDocument: readField(preRequestDocumentRef, 'preRequestDocument'),
                validityStartTime: validityStart,
                validityEndTime: validityEnd,
                primaryCause: readField(primaryCauseRef, 'PrimaryCause') === '' ? 'none' : readField(primaryCauseRef, 'PrimaryCause'),
                lightingRiskLevel: readField(lightningRiskLevelRef, 'LightningRiskLevel') === '' ? 'none' : readField(lightningRiskLevelRef, 'LightningRiskLevel'),
                warningDate: createdAt,
                requestersDepartment: readField(requestersDepartmentRef, 'requestersDepartment') || department,
                messageBodyEnglish: generalMessageRef.current?.value ?? '',
                messageCusBodyEnglish: customMessageRef.current?.value ?? '',
                areas,
                supportiveDocument: null,   // not needed to generate the message
            }, getAccessToken())
    
            // textarea is uncontrolled, so write directly into it
            if (generalMessageRef.current) generalMessageRef.current.value = message
        } catch (err) {
            setSubmitStatus({ ok: false, text: err instanceof Error ? err.message : 'Failed to generate message.' })
        } finally {
            setGenerating(false)
        }
    }

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            setFileName(event.target.files[0].name);
        } else {
            setFileName('Upload');
        }
    }

    const handleSaveAndSubmit = async () => {
        setSubmitStatus(null)

        const areas = areaTableRef.current?.getSelectedRows() ?? []

        // basic checks before we hit the server
        if (!warningId) {
            setSubmitStatus({ ok: false, text: 'Warning id has not been generated yet.' })
            return
        }
        if (!validityStart || !validityEnd) {
            setSubmitStatus({ ok: false, text: 'Validity start and end time are required.' })
            return
        }
        if (new Date(validityEnd) <= new Date(validityStart)) {
            setSubmitStatus({ ok: false, text: 'Validity end time must be after the start time.' })
            return
        }
        if (areas.length === 0) {
            setSubmitStatus({ ok: false, text: 'Select at least one area.' })
            return
        }

        setSubmitting(true)
        try {
            await submitEarlyWarning({
                triggerType: 'MANUAL',
                type: hazardType,
                warningNumber: warningId,
                warningId: flowId,
                requestName: readField(requestNameRef, 'FlowName') || warningName,
                contactNumber: readField(contactNumberRef, 'contactNumber'),
                preRequestDocument: readField(preRequestDocumentRef, 'preRequestDocument'),
                validityStartTime: validityStart,
                validityEndTime: validityEnd,
                primaryCause: readField(primaryCauseRef, 'PrimaryCause') === '' ? 'none' : readField(primaryCauseRef, 'PrimaryCause'),
                lightingRiskLevel: readField(lightningRiskLevelRef, 'LightningRiskLevel') === '' ? 'none' : readField(lightningRiskLevelRef, 'LightningRiskLevel'),
                warningDate: createdAt,
                requestersDepartment: readField(requestersDepartmentRef, 'requestersDepartment') || department,
                messageBodyEnglish: generalMessageRef.current?.value ?? '',
                messageCusBodyEnglish: customMessageRef.current?.value ?? '',
                areas,
                supportiveDocument: fileInputRef.current?.files?.[0] ?? null,
            }, getAccessToken())

            setSubmitStatus({ ok: true, text: 'Early warning submitted successfully.' })
            showAlertDialog("Success", "✅ Early warning submitted successfully!", undefined, () => window.location.reload());
        } catch (err) {
            showAlertDialog("Error", "❌ Error submitting data")
            setSubmitStatus({ ok: false, text: err instanceof Error ? err.message : 'Submission failed.' })
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className={style.MainWrapper}>
            <div className={style.Window}>
                <span>Early Warning Details ({warningName})</span>
                <div className={style.MainDetailWrapper}>
                    <div
                        className={style.FieldsWrapper}
                        ref={fieldsRef}
                        style={{
                            gridAutoFlow: 'column',
                            gridTemplateRows: `repeat(${Math.ceil(dataMap.length / 2)}, auto)`,
                        }}
                    >
                        {dataMap.map((field, index) => (
                            <InputGroup key={index} {...field} />
                        ))}
                    </div>
                    {warningIdError && (
                        <p style={{ color: 'var(--color-main-red)' }}>
                            {warningIdError} <button onClick={loadWarningKey}>retry</button>
                        </p>
                    )}
                </div>
            </div>

            <div className={style.Window}>
                <span>Early Warning Details ({warningName})</span>
                <div className={style.MainTableWrapper}>
                    <div className={style.TableWrapper}>
                        <AreaTable ref={areaTableRef} warningType={hazardType as WarningType} defaultStart={validityStart} defaultEnd={validityEnd} />
                    </div>
                </div>
            </div>

            <div className={style.Window}>
                <span>Early Warning Details (Intended Dissemination of Information to People)</span>
                <div className={style.MainMessageWrapper}>
                    <div className={style.MessageGrid}>
                        <label htmlFor="messageBodyEnglish">Message Body SMS - English</label>
                        <textarea id="messageBodyEnglish" ref={generalMessageRef} name="messageBodyEnglish" />

                        <label htmlFor="reportLink">Report Link</label>
                        <input id="reportLink" ref={reportLinkRef} name="reportLink" type="text" />
                    </div>
                    <div className={style.ButtonWrapper}>
                        <button onClick={handleGenerateEWMessageData} disabled={generating}>
                            {generating ? 'Generating...' : 'Generate General EW Message'}
                        </button>
                    </div>
                </div>
            </div>

            <div className={style.Window}>
                <span
                    style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: '1rem', cursor: 'pointer',
                        borderRadius: isMoreMassageOptionOpen ? '0.5rem 0.5rem 0 0' : '0.5rem',
                    }}
                    onClick={() => setIsMoreMessageOptionOpen(prev => !prev)}
                >
                    More Messaging Options..
                    <div>{isMoreMassageOptionOpen ? <UpIcon color='var(--color-text)' width='1rem' /> : <DownIcon color='var(--color-text)' width='1rem' />}</div>
                </span>
                <div className={`${style.MoreMessageCollapse} ${isMoreMassageOptionOpen ? style.Open : ''}`}>
                    <div className={style.MainMoreMessageWrapper}>
                        <div className={style.MessageLegendWrapper}>
                            <span>Message Placeholders</span>
                            <div>
                                <span>Warning Id: {'${warningId}'}</span>
                                <span>Warning Name: {'${warningName}'}</span>
                                <span>Area Name: {'${areaName}'}</span>
                                <span>Severity: {'${severity}'}</span>
                                <span>Validity Start Time: {'${validityStart}'}</span>
                                <span>Validity End Time: {'${validityEnd}'}</span>
                            </div>
                        </div>
                        <div className={style.MessageGrid}>
                            <label htmlFor="messageCusBodyEnglish">Local Message Body - English</label>
                            <textarea id="messageCusBodyEnglish" ref={customMessageRef} name="messageCusBodyEnglish" />
                        </div>
                    </div>
                </div>
            </div>

            <div className={style.Window}>
                <span>Early Warning Details (Additional Information to Support Approval)</span>
                <div className={style.MainSupportiveWrapper}>
                    <div>
                        <span>Supportive documents</span>
                        <div className={style.UploadContainer}>
                            <span className={style.UploadText}>{fileName}</span>
                            <label className={style.BrowseButton}>
                                Browse
                                <input type="file" ref={fileInputRef} onChange={handleFileChange} className={style.FileInput} />
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            {/* {submitStatus && (
                <p style={{ color: submitStatus.ok ? 'var(--color-text)' : 'var(--color-main-red)', textAlign: 'center' }}>
                    {submitStatus.text}
                </p>
            )} */}

            <div className={style.ButtonWrapper}>
                <button className={style.ButtonNormal} style={{ backgroundColor: 'var(--color-main-red)' }} disabled={submitting}>cancel</button>
                <button className={style.ButtonNormal} onClick={() => showAlertDialog(
										"Confirm Submit",
										"Are you sure you want to submit this awareness?",
										() => handleSaveAndSubmit()
									)} disabled={submitting}>
                    {submitting ? 'submitting...' : 'save & submit'}
                </button>
            </div>
            {alertDialogState && (
				<AlertDialog
					title={alertTitle}
					description={alertDescription}
					windowState={alertDialogState}
					setWindowState={setAlertDialogState}
					function={alertYesCallback}
					okFunction={alertOkCallback}
				/>
			)}
            {submitting && (
                <LoadingScreen />
            )}
        </div>
    )
}

export default EW